import "./importCsvPreviewTable.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import {
  CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT,
  CSV_IMPORT_TABLE_CELL_MAX_WIDTH,
  CSV_IMPORT_TABLE_CELL_MIN_WIDTH
} from "../../../constants/importExportFile";
import ImportCsvColumnInfo from "./ImportCsvColumnInfo";
import ImportCsvColumnMatch from "./ImportCsvColumnMatch";
import ImportCsvColumnTypeSelector from "./ImportCsvColumnTypeSelector";
import ImportCsvTableCell from "./ImportCsvTableCell";

const fixCellsByColumnCount = (cells, columnCount) => {
  const cellsCount = cells.count();
  if (cellsCount < columnCount) {
    return cells.concat(Array(columnCount - cellsCount).fill(""));
  } else if (cellsCount > columnCount) {
    return cells.slice(0, columnCount);
  }
  return cells;
};

const initColumnWidths = columnCount => List(Array(columnCount).fill(CSV_IMPORT_TABLE_CELL_MIN_WIDTH));

const isColumnTypeOptionDisabled = (typeValue, selectedType, columnTypes) => {
  return (
    typeValue !== CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT && typeValue !== selectedType && columnTypes.includes(typeValue)
  );
};

class ImportCsvPreviewTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      containerWidth: null,
      containerHeight: null,
      columnWidths: initColumnWidths(props.columnCount)
    };

    this.containerRef = React.createRef();

    this.updateColumnWidths = this.updateColumnWidths.bind(this);
  }

  componentDidMount() {}

  componentDidUpdate(prevProps) {
    this.updateContainerDimensions();
    // Note: this is a simple checking if the data has changed
    if (prevProps.columnCount !== this.props.columnCount) {
      this.resetColumnWidths();
    }
  }

  resetColumnWidths() {
    this.setState({ columnWidths: initColumnWidths(this.props.columnCount) });
  }

  updateContainerDimensions() {
    const container = this.containerRef.current;
    if (container && container.scrollWidth !== this.state.containerWidth) {
      this.setState({ containerWidth: container.scrollWidth });
    }
    if (container && container.scrollHeight !== this.state.containerHeight) {
      this.setState({ containerHeight: container.scrollHeight });
      this.props.onChangeTableHeight(container.scrollHeight);
    }
  }

  filterColumnTypeOptions(selectedType) {
    return this.props.columnTypeOptions.map(cto => {
      let newOption = Object.assign({}, cto);
      newOption.disabled = isColumnTypeOptionDisabled(cto.value, selectedType, this.props.columnTypes);
      // Check children
      if (newOption.children && newOption.children.length > 0) {
        newOption.children = newOption.children.map(c => {
          c.disabled = isColumnTypeOptionDisabled(c.value, selectedType, this.props.columnTypes);
          return c;
        });
      }
      return newOption;
    });
  }

  updateColumnWidths(columnIndex, newWidth) {
    const { columnWidths } = this.state;
    const acceptWidth = Math.min(CSV_IMPORT_TABLE_CELL_MAX_WIDTH, newWidth);
    if (columnWidths.get(columnIndex) < acceptWidth) {
      this.setState({ columnWidths: columnWidths.set(columnIndex, acceptWidth) });
    }
  }

  getColumnTypesRow() {
    return this.props.columnTypes.map((ct, index) => {
      return (
        <ImportCsvTableCell
          key={index}
          firstColumn={index === 0}
          width={this.state.columnWidths.get(index)}
          onGetNewWidth={newWidth => this.updateColumnWidths(index, newWidth)}
        >
          <ImportCsvColumnTypeSelector
            columnType={ct}
            options={this.filterColumnTypeOptions(ct)}
            onChange={newValue => this.props.onColumnTypeChange(index, newValue)}
          />
          {
            <ImportCsvColumnInfo
              columnType={ct}
              value={this.props.columnInfoOptions.get(ct)}
              onChange={value => this.props.onColumnInfoOptionsChange(ct, value)}
              typesById={this.props.typesById}
              referencesById={this.props.referencesById}
            />
          }
          <ImportCsvColumnMatch columnType={ct} {...this.props.columnMatchCounts.get(ct).toJS()} />
        </ImportCsvTableCell>
      );
    });
  }

  getHeaderRow() {
    const { data } = this.props;
    return Array(this.props.columnCount)
      .fill("")
      .map((c, cIndex) => {
        return (
          <ImportCsvTableCell
            isHeader
            key={cIndex}
            firstColumn={cIndex === 0}
            firstRow
            width={this.state.columnWidths.get(cIndex)}
            onGetNewWidth={newWidth => this.updateColumnWidths(cIndex, newWidth)}
          >
            {
              // Show the header strings if it has header.
              // Otherwise, show the Column A, Column B ...
              this.props.hasHeaderRow && data.get(0)
                ? data.get(0).get(cIndex)
                : `Column ${String.fromCharCode(cIndex + 65)}`
            }
          </ImportCsvTableCell>
        );
      });
  }

  getDataRows() {
    const { data, columnCount } = this.props;
    const dataList = this.props.hasHeaderRow ? data.shift() : data;
    return dataList.slice(0, this.props.maxRows).map(cells =>
      fixCellsByColumnCount(cells, columnCount).map((cell, cIndex) => (
        <ImportCsvTableCell
          isData
          key={cIndex}
          firstColumn={cIndex === 0}
          width={this.state.columnWidths.get(cIndex)}
          onGetNewWidth={newWidth => this.updateColumnWidths(cIndex, newWidth)}
        >
          {cell}
        </ImportCsvTableCell>
      ))
    );
  }

  render() {
    const tableStyles = {
      gridTemplateColumns: `repeat(${this.props.columnCount}, auto)`,
      gridTemplateRows: `repeat(${this.props.data.count()}, auto)`
    };

    return (
      <Fragment>
        <div className="importCsvPreviewTableContainer" ref={this.containerRef}>
          <div className="importCsvPreviewTable" style={tableStyles}>
            {this.getHeaderRow()}
            {this.getColumnTypesRow()}
            {this.getDataRows()}
          </div>
        </div>
      </Fragment>
    );
  }
}

ImportCsvPreviewTable.defaultProps = {
  data: List(),
  columnCount: 0
};

ImportCsvPreviewTable.propTypes = {
  hasHeaderRow: PropTypes.bool,
  data: ImmutablePropTypes.list.isRequired,
  columnCount: PropTypes.number.isRequired,
  columnTypes: ImmutablePropTypes.list.isRequired,
  columnTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnMatchCounts: ImmutablePropTypes.map.isRequired,
  onColumnTypeChange: PropTypes.func.isRequired,
  columnInfoOptions: ImmutablePropTypes.map.isRequired,
  onColumnInfoOptionsChange: PropTypes.func.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  referencesById: ImmutablePropTypes.map.isRequired,
  onChangeTableHeight: PropTypes.func.isRequired,
  maxRows: PropTypes.number.isRequired
};

export default ImportCsvPreviewTable;
